import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState, isValidElement, cloneElement} from 'react';
import {
	Box,
	Paper,
	Typography,
	alpha,
	IconButton,
	Divider,
} from '@mui/material';
import {css} from '@emotion/css';
import EntryMenu, { RowMenuItem, RowMenuOptionReorder, RowMenuOptionEffect, RowMenuOptionArchive, RowMenuOptionUnarchive, RowMenuOptionDelete, RowMenuProps, RowMenuOptionGoTo, RowMenuOption } from './EntryMenu';
import CenteredEmptyState from './CenteredEmptyState';
import { PropertyDefinition } from '../types';
import { entryPropertyGetReadableValue, entryPropertyGetSecondaryValue, extractPropertyValueUrls } from '../common/util';
import { RepositoryEntriesRowDto } from '../services/repositoryEntries';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useThemeContext } from '../ThemeProvider';
import { ExternalLinkOverlay } from './ExternalLinkOverlay';

type EntriesListItemMenuProps = Omit<RowMenuProps,'onRowReorderClick' | 'onMenuBtnClick' | 'options'>

export interface EntriesListDragHandleProps {
	onMouseDown: (e: React.MouseEvent) => void;
	// onTouchStart: (e: React.TouchEvent) => void;
	// onTouchEnd: (e: React.TouchEvent) => void;
}

export interface EntriesListRowEventHandlers {
	onMouseEnter: (e: React.MouseEvent) => void;
	onTouchEnd: (e: React.TouchEvent) => void;
}

interface EntriesListItemProps {
	id: string;
	label: string;
	entry: RepositoryEntriesRowDto;
	propertyDefinitions: PropertyDefinition[];
	onClick?: (e: React.MouseEvent) => void;
	setMenuAnchorEl?: any;
	isSelected?: boolean;
	onEntryFocus?: (e: React.FocusEvent<HTMLDivElement>) => void;
	onEntryBlur?: () => void;
	disabled?: boolean;
	dragHandleProps?: EntriesListDragHandleProps;
	rowEventHandlers?: EntriesListRowEventHandlers;
	isDragging?: boolean;
	showHoldToCancelNotice?: boolean;
	reorderCommittedRef?: React.MutableRefObject<boolean>;
	reorderActiveRef?: React.MutableRefObject<boolean>;
	onPointerDown?: (e: React.PointerEvent) => void;
	onPointerMove?: (e: React.PointerEvent) => void;
	onPointerUp?: (e: React.PointerEvent) => void;
	onPointerLeave?: (e: React.PointerEvent) => void;
	onPointerCancel?: (e: React.PointerEvent) => void;
	onClickDragHandle?: (id: string) => void;
	repositorySingular?: string;
	collectionColor?: string;
}

interface Props {
	children: ReactNode;
	rowMenuProps?: EntriesListItemMenuProps;
	onRowMenuGoToClick?: (id: string) => void;
	onRowMenuArchiveClick?: (id: string) => void;
	onRowMenuUnarchiveClick?: (id: string) => void;
	onRowMenuDeleteClick?: (id: string) => void;
	onReorder?: (orderedIds: string[]) => void;
	disableReordering?: boolean;
	isLoading?: boolean;
	onTouchReorderChange?: (isActive: boolean) => void;
}

export const EntriesListItem = ({ id, entry, propertyDefinitions, onClick, isSelected, dragHandleProps, isDragging, reorderCommittedRef, disabled, onClickDragHandle, repositorySingular, collectionColor }: EntriesListItemProps) => {
	const classes = {
		maxHeight2Lines: css`
			overflow: hidden;
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
		`,
	};
	const { isDarkModeOn } = useThemeContext();
	const isDragHandle = Boolean(dragHandleProps && !isDragging);
	const handleClick = (e: React.MouseEvent) => {
		if (reorderCommittedRef?.current) {
			e.preventDefault();
			reorderCommittedRef.current = false;
			return;
		}
		onClick?.(e);
	};
	const entryTitle = entryPropertyGetReadableValue(propertyDefinitions[0], entry.data[propertyDefinitions[0].slug]);
	const entrySecondaryTitle = entryPropertyGetSecondaryValue(propertyDefinitions[0], entry.data[propertyDefinitions[0].slug]);
	const entryTitleUrls = Array.from(new Set(extractPropertyValueUrls(propertyDefinitions[0], entry.data[propertyDefinitions[0].slug])));
	return <Box
		width={1}
		key={id}
		aria-label={isDragHandle ? 'Drag to reorder' : undefined}
		data-entry-id={id}
		sx={{
			boxShadow: '0 2px 4px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.1)',
			...((isDragging) && {
				backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.15),
			}),
			...((isSelected && {
				borderColor: 'divider',
			})),
			padding: 1.25,
			...(repositorySingular && { position: 'relative', pb: 2.5 }),
		}}
		component={Paper}
		onClick={handleClick}
	>
		<Box display="flex" flexDirection="column" gap={1}>
			<Box display="flex" flexDirection="row" gap={1} alignItems="center" justifyContent="space-between">
				{onClickDragHandle && <IconButton sx={{color: 'text.secondary'}} onClick={() => onClickDragHandle(id)}>
					<DragIndicatorIcon sx={{opacity: isDragging ? 1 : 0.4}} />
				</IconButton>}
				<Box flex={1} sx={{position: 'relative', overflow: 'hidden', wordBreak: 'break-word'}}>
					<Typography mt={0.25} variant="h6" className={classes.maxHeight2Lines} color={disabled ? 'text.disabled' : 'text.primary'}>{entryTitle}</Typography>
					{entrySecondaryTitle && <Typography variant="caption" color="primary.light" mt={-1.5}>{entrySecondaryTitle}</Typography>}
					{entryTitleUrls.length > 0 && <ExternalLinkOverlay urls={entryTitleUrls} />}
				</Box>
			</Box>
			{propertyDefinitions.length > 1 && <Divider sx={{opacity: isDarkModeOn ? 1 : 0.4}}/>}
			{propertyDefinitions.slice(1, 3)
				.map((def) => {
					const rawValue = entry.data[def.slug];
					const primaryValue = entryPropertyGetReadableValue(def, rawValue);
					const secondaryValue = entryPropertyGetSecondaryValue(def, rawValue);
					const urls = Array.from(new Set(extractPropertyValueUrls(def, rawValue)));
					return(
						<Box key={`${id}-${def.slug}`} display="flex" flexDirection="column" gap={0.25} sx={{position: 'relative'}}>
							<Typography variant="caption" sx={{ color: 'text.secondary' }}>{def.label}</Typography>
							<Typography variant="body2" className={classes.maxHeight2Lines} sx={{ color: disabled ? 'text.disabled' : 'text.primary' }}>{primaryValue || '-'}</Typography>
							{secondaryValue && <Typography variant="caption" color="primary.light">{secondaryValue}</Typography>}
							{urls.length > 0 && <ExternalLinkOverlay urls={urls} />}
						</Box>
					);})}
		</Box>
		{repositorySingular && (
			<Box display="flex" alignItems="center" gap={0.5} sx={{ position: 'absolute', bottom: 0, right: 12 }}>
				<Typography color="action.disabled" variant="caption" sx={{ fontSize: '0.625rem', lineHeight: 1.5 }}>{repositorySingular}</Typography>
				{collectionColor && <Box sx={{ mt: -1, width: 6, height: 6, borderRadius: '50%', backgroundColor: collectionColor }} />}
			</Box>
		)}
	</Box>;
};

const EntriesList: React.FC<Props> = ({children, rowMenuProps, onReorder, disableReordering, onRowMenuGoToClick, onRowMenuArchiveClick, onRowMenuUnarchiveClick, onRowMenuDeleteClick, isLoading, onTouchReorderChange}) => {
	const [dragItemId, setDragItemId] = useState<string | null>(null);
	const [touchItemId, setTouchItemId] = useState<string | null>(null);
	const [localOrder, setLocalOrder] = useState<string[] | null>(null);
	const reorderCommittedRef = useRef(false);
	const reorderActiveRef = useRef(false);
	const localOrderRef = useRef<string[] | null>(null);
	localOrderRef.current = localOrder;

	useEffect(() => {
		onTouchReorderChange?.(Boolean(touchItemId));
	}, [touchItemId, onTouchReorderChange]);

	const entryIds = useMemo(() =>
		React.Children.map(children, (c) => isValidElement(c) && (c as React.ReactElement<{ id: string }>).props?.id)?.filter(Boolean) as string[] ?? [],
	[children]
	);
	const childMap = useMemo(() => {
		const m = new Map<string, React.ReactElement>();
		React.Children.forEach(children, (c) => {
			if (isValidElement(c) && (c as React.ReactElement<{ id: string }>).props?.id) {
				m.set((c as React.ReactElement<{ id: string }>).props.id, c as React.ReactElement);
			}
		});
		return m;
	}, [children]);

	const order = localOrder ?? entryIds;
	const reorderItemId = dragItemId ?? touchItemId;

	const commitReorder = useCallback(() => {
		const order = localOrderRef.current;
		if (order && onReorder) {
			reorderCommittedRef.current = true;
			onReorder(order);
		}
	}, [onReorder]);

	/** Drop local order overlay once parent data order matches (same pattern as EntriesTable). */
	useEffect(() => {
		if (!localOrder?.length) {
			return;
		}
		if (entryIds.length !== localOrder.length) {
			return;
		}
		if (entryIds.every((id, i) => id === localOrder[i])) {
			setLocalOrder(null);
		}
	}, [entryIds, localOrder]);

	const applyLocalOrder = useCallback((targetId: string) => {
		const movingId = dragItemId ?? touchItemId;
		if (!movingId || movingId === targetId) return;
		const current = localOrder ?? entryIds;
		const fromIndex = current.indexOf(movingId);
		const toIndex = current.indexOf(targetId);
		if (fromIndex === -1 || toIndex === -1) return;
		const next = [...current];
		next.splice(fromIndex, 1);
		next.splice(toIndex, 0, movingId);
		setLocalOrder(next);
	}, [dragItemId, touchItemId, localOrder, entryIds]);

	const handlePointerDown = useCallback((e: React.PointerEvent, id: string) => {
		if ((e.target as HTMLElement).closest('button')) return;
		// Only allow mouse-driven reorder to avoid conflicting with native touch scroll.
		if (e.pointerType !== 'mouse') return;
		e.preventDefault();
		setDragItemId(id);
		(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
	}, []);

	const handlePointerMove = useCallback((e: React.PointerEvent) => {
		if (!dragItemId) return;
		const el = document.elementFromPoint(e.clientX, e.clientY);
		const row = el?.closest('[data-entry-id]');
		const targetId = row?.getAttribute('data-entry-id');
		if (targetId && targetId !== dragItemId) {
			applyLocalOrder(targetId);
		}
	}, [dragItemId, applyLocalOrder]);

	const handlePointerUp = useCallback((e: React.PointerEvent) => {
		(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
		if (dragItemId) {
			if (localOrderRef.current) {
				commitReorder();
			}
			setDragItemId(null);
		}
	}, [dragItemId, commitReorder]);

	const handlePointerCancel = useCallback((e: React.PointerEvent) => {
		(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
		if (dragItemId) {
			setDragItemId(null);
			setLocalOrder(null);
		}
	}, [dragItemId]);

	const handleRowMenuReorderClick = useCallback((item: RowMenuItem) => {
		rowMenuProps?.onClose();
		setTouchItemId(item.id);
		setLocalOrder(entryIds);
	}, [rowMenuProps, entryIds]);
	const handleRowMenuMoveToTopClick = useCallback((item: RowMenuItem) => {
		setTouchItemId(item.id);
	}, []);

	// Touch reorder: after choosing "Reorder" in the menu, tapping another entry moves the item there and exits reorder mode.
	const handleTouchReorderTap = useCallback((e: React.MouseEvent, targetId: string): boolean => {
		if (!touchItemId) return false;
		if (touchItemId === targetId) {
			setTouchItemId(null);
			setLocalOrder(null);
			e.preventDefault();
			return true;
		}
		const current = localOrder ?? entryIds;
		const fromIndex = current.indexOf(touchItemId);
		const toIndex = current.indexOf(targetId);
		if (fromIndex === -1 || toIndex === -1) return false;
		const next = [...current];
		next.splice(fromIndex, 1);
		next.splice(toIndex, 0, touchItemId);
		onReorder?.(next);
		setTouchItemId(null);
		setLocalOrder(next);
		e.preventDefault();
		return true;
	}, [touchItemId, localOrder, entryIds, onReorder]);

	const canReorder = Boolean(onReorder && !disableReordering && entryIds.length > 1);

	const rowMenuOptions: RowMenuOption[] = [
		{
			content: RowMenuOptionGoTo,
			onClick: (item: RowMenuItem) => onRowMenuGoToClick?.(item.id),
			contentProps: {keyboardShortcut: true},
			key: 'goOption',
		},{
			content: RowMenuOptionReorder,
			onClick: RowMenuOptionEffect.Reorder,
			displayCondition: (item: RowMenuItem) => canReorder && !item.isArchived,
			key: 'reorderOption',
		}, {
			content: RowMenuOptionArchive,
			onClick: (item: RowMenuItem) => onRowMenuArchiveClick?.(item.id),
			displayCondition: (item: RowMenuItem) => Boolean(!item.isArchived),
			key: 'archiveOption',
		}, {
			content: RowMenuOptionUnarchive,
			onClick: (item: RowMenuItem) => onRowMenuUnarchiveClick?.(item.id),
			displayCondition: (item: RowMenuItem) => Boolean(item.isArchived),
			key: 'unarchiveOption',
		}, {
			content: RowMenuOptionDelete,
			onClick: (item: RowMenuItem) => onRowMenuDeleteClick?.(item.id),
			key: 'deleteOption',
		}
	];

	const renderedChildren = useMemo(() => {
		return order
			.map((id) => {
				const child = childMap.get(id);
				if (!child) return null;
				const originalOnClick = (child as React.ReactElement<EntriesListItemProps>).props.onClick;
				return cloneElement(child as React.ReactElement<EntriesListItemProps>, {
					key: id,
					onClick: canReorder
						? (e: React.MouseEvent) => {
							if (handleTouchReorderTap(e, id)) return;
							originalOnClick?.(e);
						}
						: originalOnClick,
					// Do NOT use handlePointerUp on pointerleave: the row is fixed, so leaving it (e.g. moving to another row) would end the drag prematurely. End only on pointerup/pointercancel.
					onPointerLeave: undefined,
					onPointerCancel: canReorder ? handlePointerCancel : undefined,
					isDragging: reorderItemId === id,
					reorderCommittedRef,
					reorderActiveRef,
					onClickDragHandle: canReorder ? () => {
						setTouchItemId(id);
					} : undefined,
				});
			})
			.filter(Boolean);
	}, [order, childMap, canReorder, reorderItemId, handleTouchReorderTap, handlePointerDown, handlePointerMove, handlePointerUp, handlePointerCancel]);

	return <>
		<Box
			flex={1}
			display='flex'
			flexDirection='column'
			justifyContent='center'
			gap={1}
			p={1}
			sx={{ position: 'relative', minHeight: 120, mb: 9 }}
		>
			{!order.length && !isLoading && <CenteredEmptyState />}
			{renderedChildren}
		</Box>
		{rowMenuProps ? <EntryMenu 
			options={rowMenuOptions}
			anchorEl={rowMenuProps.anchorEl}
			onClose={rowMenuProps.onClose}
			item={rowMenuProps.item}
			anchorOrigin={rowMenuProps.anchorOrigin}
			transformOrigin={rowMenuProps.transformOrigin}
			onRowReorderClick={handleRowMenuReorderClick}
			onRowMoveToTopClick={handleRowMenuMoveToTopClick}
		/> : null}
	</>;
};

export default EntriesList;